import { MapsWidget } from '@/components/MapsWidget';
export const frontmatter = {
  category: 'atracao',
  title: 'Conchas Lighthouse: How to Go Up and the 360° View of Ilha do Mel',
  excerpt: 'Complete guide to climbing the Farol das Conchas lighthouse on Ilha do Mel. Trail, schedules, entrance fee, and the best time to watch the sunset from the top of the island.',
  publishedAt: '2026-04-24',
  author: 'Guia Ilha do Mel',
  tags: ['farol', 'trilha', 'passeio', 'vista-panoramica'],
  coverImage: '/images/praia-do-farol-hero.jpg',
  readingTime: '6 min',
};

# Conchas Lighthouse: How to Go Up and the 360° View

The **Conchas Lighthouse** is the most iconic postcard image of Ilha do Mel (Honey Island). Located atop Morro do Farol (Lighthouse Hill), it offers one of the most spectacular views of the Paraná coast—sea, Atlantic rainforest, and villages all from a single vantage point.

In this guide, we explain **how to get there**, **how much it costs**, **what to expect** from the ascent, and the **best time** to be up there.

---

Where is the Conchas Lighthouse located?

The lighthouse is located on **Morro do Farol** (Lighthouse Hill), in the center of the island, between Farol Beach and Praia Grande Beach. It is accessible via a marked trail that starts at the base of the hill.

| Information | Details |

| ------------------- | -------------------------------- |

| **Location** | Morro do Farol, Ilha do Mel — PR |

| **Altitude** | 150 meters above sea level |

| **Access** | Dirt and stone trail |

| **Ascent time** | 15-20 minutes (easy pace) |

| **Difficulty** | Easy to moderate |

---

How to get to the trailhead

Departing from Nova Brasília

1. Follow the main trail south (towards Encantadas)
2. After about 10 minutes of walking, there will be a sign indicating "Lighthouse" on the left
3. The lighthouse trail climbs the hillside

Departing from Encantadas

1. Follow the main trail north (towards Nova Brasília)
2. The lighthouse sign will be on the right after passing Praia do Farol (Lighthouse Beach)
3. Total distance: ~25-30 min walk from the Encantadas pier

---

The trail: what to expect

The climb is **short but steep in some sections**. It doesn't require special equipment, but some precautions are important:

- **Footwear:** Wear sneakers with traction rubber. The rocks can be slippery, especially after rain.

- **Water:** Bring a small bottle. There are no vendors at the top.

- **Mosquitoes:** Insect repellent helps in the dense forest areas.

- **Umbrella or raincoat:** The weather changes quickly on the island.

**Local tip:** Go up in the late afternoon (1 hour before sunset). The golden light over the sea is cinematic — and the cooler temperature makes the climb more comfortable.

---

At the top: the 360° view

Upon reaching the top, you will find:

- The **historic lighthouse** (built in 1872, still operational)

- An **observation area** surrounded by safety railings

- Informative plaques about the history of the lighthouse and the island

What can be seen from each side:

| Direction | View |

| --------- | -------------------------------------------------- |

| **East** | Atlantic Ocean and the endless horizon |

| **South** | Encantadas Beach and the mainland in the background |

| **West** | Paranaguá Bay, mangroves and Serra do Mar mountain range |

| **North** | Praia Grande, Nova Brasília and the coastal beaches |

On clear days, it's possible to see the **Port of Paranaguá** and even the city of Paranaguá in the distance.

---

Schedules and prices

| Item | Detail |

| ---------------------------- | -------------------------------------- |

| **Opening Hours** | 9am to 5pm (daily) |

| **Entrance** | Free |

| **Best Time** | 4:30pm-5pm (sunset) |

| **Restrictions** | Climbing on the lighthouse structures is prohibited |

**Attention:** The lighthouse closes at dusk for safety reasons. Do not stay at the top after closing time — descending in the dark is dangerous.

---

## Photos: how to capture the view

- **Wide-angle lens** (16-24mm) captures the full expanse of the view
- **Golden hour** (1 hour before sunset) offers warm light and long shadows
- **Lightweight tripod** useful for long exposures of the sea at dusk

- **Drone:** flying overhead is permitted, but be aware of strong winds

---

Visitation Rules

- Do not leave trash on the trail or at the top.
- Do not scratch or vandalize the lighthouse structures.
- Respect the silence — it is a place for contemplation.
- Pets are allowed on the trail, but must be on a leash.

---

Combine with other tours

The Farol das Conchas lighthouse is located halfway between the two villages. Combine your visit with:

- **Farol Beach** — beach just below the hill, perfect for relaxing after the climb
- **Enchanted Trail** — continuation of the main trail to the southern village
- **Big Beach** — the largest stretch of sand on the island, a 10-minute walk away

<MapsWidget query="Farol das Conchas Ilha do Mel" />

---

Want to plan a complete itinerary for Ilha do Mel? Our [complete guide to Ilha do Mel](/guide-complete) has trails, beaches, restaurants, and tips from locals so you can enjoy every corner of the island.